import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Col, Row } from 'reactstrap';
import ResourceProperty from '../../../common/resource/ResourceProperty';
import LinkToModuleResource from '../../../common/resource/LinkToModuleResource';
import EnumStatus from '../../../common/EnumStatus';
import Badge from '../../../common/Badge';
import {
  COMMERCIAL_STATUS,
  END_DATE_SOURCE,
  STATUS_EVENT_TYPE,
} from '../../../../config/constants';
import {
  BUS_MAINTENANCE_STAY_PATH,
  ITINERARY_FOR_COUNTER_PATH,
} from '../../../../config/paths';
import { tzNormalizeDate } from '../../../../utils/date';
import { DATE_TIME_FORMAT } from '../../../../config/locale';

const STATUS_PRECEDENCE = [
  COMMERCIAL_STATUS.ON_TRIP,
  COMMERCIAL_STATUS.NOT_PROGRAMMABLE,
  COMMERCIAL_STATUS.PROGRAMMED,
  COMMERCIAL_STATUS.STANDBY,
  COMMERCIAL_STATUS.AVAILABLE,
]
  .map(({ label }) => label)
  .join(' > ');

const FLAG_LABELS = [
  { key: 'hasItineraryNow', label: 'Itinerario en curso' },
  { key: 'hasFutureItinerary', label: 'Itinerario futuro' },
  { key: 'hasBlockingMaintenanceNow', label: 'Mantenimiento activo' },
  { key: 'hasActiveRetentionNow', label: 'Retén activo' },
  { key: 'hasDriverAssigned', label: 'Conductor asignado' },
];

const formatDate = (value) =>
  value ? tzNormalizeDate({ date: value, format: DATE_TIME_FORMAT }) : '-';

const BusCurrentStatusEvidence = ({ bus }) => {
  const { currentStatus, currentStatusSince, statusEvidence } = bus;

  const isAvailable = currentStatus === COMMERCIAL_STATUS.AVAILABLE.value;
  const hasEvent = !!(statusEvidence && statusEvidence.eventType);

  const {
    eventType = null,
    eventId = null,
    eventCode = null,
    label = null,
    startDate = null,
    endDate = null,
    endDateSource = null,
  } = statusEvidence || {};

  let eventLink = null;

  if (eventId && eventType === STATUS_EVENT_TYPE.ITINERARY.value) {
    eventLink = (
      <LinkToModuleResource
        href={`${ITINERARY_FOR_COUNTER_PATH}/${eventId}`}
        text="Ver itinerario"
        isExternalPath
      />
    );
  } else if (eventId && eventType === STATUS_EVENT_TYPE.MAINTENANCE.value) {
    eventLink = (
      <LinkToModuleResource
        href={`${BUS_MAINTENANCE_STAY_PATH}/${eventId}`}
        text="Ver estadía de mantenimiento"
      />
    );
  }

  return (
    <div className="p-3 bg-light">
      <Row>
        <Col md={6}>
          <h6>Evento que determinó el estado</h6>
          {hasEvent ? (
            <>
              <ResourceProperty label="Tipo:">
                <EnumStatus
                  enumObject={STATUS_EVENT_TYPE}
                  enumValue={eventType}
                />
              </ResourceProperty>
              {eventCode ? (
                <ResourceProperty label="Código:">{eventCode}</ResourceProperty>
              ) : null}
              <ResourceProperty label="Detalle:">
                {label || '-'}
              </ResourceProperty>
              <ResourceProperty label="Inicio:">
                {formatDate(startDate)}
              </ResourceProperty>
              <ResourceProperty label="Fin:">
                {formatDate(endDate)}
              </ResourceProperty>
              <ResourceProperty label="Origen de la fecha de fin:">
                <EnumStatus
                  enumObject={END_DATE_SOURCE}
                  enumValue={endDateSource}
                />
              </ResourceProperty>
              {endDateSource === END_DATE_SOURCE.GRACE_PERIOD.value ? (
                <Alert color="warning" className="py-2 small">
                  El mantenimiento no tiene fecha de fin cargada: el sistema
                  asumió la llegada + 5 horas.
                </Alert>
              ) : null}
              {eventLink}
            </>
          ) : (
            <>
              <ResourceProperty label="Libre desde:">
                {formatDate(endDate)}
              </ResourceProperty>
              {isAvailable && !endDate ? (
                <Alert color="warning" className="py-2 small">
                  Sin evidencia ni evento previo. Puede ser un bus realmente
                  libre o un bus en ruta cuyo itinerario no tiene hora estimada
                  de llegada cargada; se corrige el dato en origen.
                </Alert>
              ) : null}
            </>
          )}
        </Col>
        <Col md={6}>
          <h6>Condiciones evaluadas</h6>
          {FLAG_LABELS.map(({ key, label: flagLabel }) => (
            <ResourceProperty key={key} label={`${flagLabel}:`}>
              <Badge
                color={bus[key] ? 'success' : 'secondary'}
                text={bus[key] ? 'Sí' : 'No'}
              />
            </ResourceProperty>
          ))}
          <ResourceProperty label="En este estado desde:">
            {formatDate(currentStatusSince)}
          </ResourceProperty>
          <div className="text-muted small mt-2">
            {`Precedencia (gana el primero que aplica): ${STATUS_PRECEDENCE}. `}
            Por eso un bus con retén activo y un itinerario futuro aparece como
            &quot;Programado&quot;.
          </div>
        </Col>
      </Row>
    </div>
  );
};

BusCurrentStatusEvidence.propTypes = {
  bus: PropTypes.shape({
    currentStatus: PropTypes.string,
    currentStatusSince: PropTypes.string,
    hasItineraryNow: PropTypes.bool,
    hasFutureItinerary: PropTypes.bool,
    hasBlockingMaintenanceNow: PropTypes.bool,
    hasActiveRetentionNow: PropTypes.bool,
    hasDriverAssigned: PropTypes.bool,
    statusEvidence: PropTypes.shape({
      eventType: PropTypes.string,
      eventId: PropTypes.number,
      eventCode: PropTypes.string,
      label: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      endDateSource: PropTypes.string,
    }),
  }).isRequired,
};

export default BusCurrentStatusEvidence;
